import { ChangeDetectionStrategy, Component, computed, effect, input, signal, untracked } from '@angular/core';
import { isScreeningVideoUrl } from '@/app/pages/manage-brief-tabs-screen/data/screening-results.adapter';
import { ScreeningContentItem } from '@/app/pages/manage-brief-tabs-screen/data/screening-results.model';
import { ScreeningDetailsDrawerComponent } from '../screening-details-drawer/screening-details-drawer';

/**
 * One screened post: portrait media (video, image, or carousel) and the issue / approved counts.
 */
@Component({
  selector: 'scr-content-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ScreeningDetailsDrawerComponent],
  templateUrl: './content-card.html',
})
export class ContentCard {
  readonly item = input.required<ScreeningContentItem>();
  /** 1-based position, used for the image alt text. */
  readonly position = input(1);
  readonly briefId = input.required<string>();
  /** Set on the cohort list. The per-creator report already names the creator. */
  readonly creatorName = input<string | undefined>(undefined);
  readonly slideIndex = signal(0);

  private trackedId = '';

  readonly alt = computed(() => `Content ${this.position()}`);

  readonly currentUrl = computed(() => {
    const urls = this.item().contentUrls;
    return urls[this.slideIndex()] ?? urls[0] ?? '';
  });

  /** Single-item list so a slide change recreates the video or image element. */
  readonly currentSlide = computed(() => {
    const url = this.currentUrl();
    return url ? [url] : [];
  });

  readonly currentIsVideo = computed(() => isScreeningVideoUrl(this.currentUrl()));

  readonly issuesClass = computed(() =>
    this.item().issues > 0
      ? 'm-0 font-heading text-sm font-bold tabular-nums text-red-500 dark:text-red-400'
      : 'm-0 font-heading text-sm font-bold tabular-nums text-foreground',
  );

  constructor() {
    effect(() => {
      const id = this.item().id;
      untracked(() => {
        if (this.trackedId !== id) {
          this.trackedId = id;
          this.slideIndex.set(0);
        }
      });
    });
  }

  previous(): void {
    const count = this.item().contentUrls.length;
    if (count <= 1) {
      return;
    }
    this.slideIndex.update((index) => (index === 0 ? count - 1 : index - 1));
  }

  next(): void {
    const count = this.item().contentUrls.length;
    if (count <= 1) {
      return;
    }
    this.slideIndex.update((index) => (index === count - 1 ? 0 : index + 1));
  }
}
